/**
 * Firebase-backed data client.
 *
 * Exposes the same fluent API surface the app previously used with Supabase
 * (from().select().eq().order(), auth, storage, functions.invoke, channel)
 * but implemented entirely on Firebase: Firestore, Firebase Auth,
 * Cloud Storage and Cloud Functions.
 *
 * Design notes:
 *  - Equality filters (.eq) run server-side as Firestore `where` clauses, so
 *    security rules can prove queries (e.g. is_published == true). Everything
 *    else (neq, order, limit, single) runs client-side — collections here are
 *    small CMS content sets, and this avoids composite-index management.
 *  - Every document stores its own id in an `id` field (mirroring the doc id)
 *    so `.eq("id", ...)` works as a normal query.
 *  - Timestamps are ISO-8601 strings, which sort correctly as strings.
 */
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  type QueryConstraint,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  deleteObject,
  listAll,
  getMetadata,
} from "firebase/storage";
import { auth, firestore, storage, FIREBASE_STORAGE_BUCKET, FUNCTIONS_BASE_URL } from "./config";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

export interface AuthUser {
  id: string;
  email?: string;
}

export interface Session {
  user: AuthUser;
}

export interface BackendError {
  message: string;
  code?: string;
}

interface Result<T> {
  data: T;
  error: BackendError | null;
  count?: number | null;
}

// Rows are dynamically shaped documents; callers cast to their own models,
// matching the ergonomics of the previous generated Supabase types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyResult = Result<any>;

type Row = Record<string, unknown> & { id?: string };

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

const mapUser = (u: FirebaseUser): AuthUser => ({ id: u.uid, email: u.email ?? undefined });

const toError = (e: unknown): BackendError => {
  if (e && typeof e === "object" && "message" in e) {
    const err = e as { message: string; code?: string };
    return { message: err.message, code: err.code };
  }
  return { message: String(e) };
};

const isPermissionDenied = (e: unknown) =>
  !!e && typeof e === "object" && "code" in e && (e as { code?: string }).code === "permission-denied";

// Column defaults previously enforced by Postgres. Applied on insert so the
// rest of the app (filters, counts) keeps working identically.
const INSERT_DEFAULTS: Record<string, Record<string, unknown>> = {
  contact_submissions: { status: "new" },
  blog_posts: { status: "draft", sort_order: 0 },
  projects: { is_published: false, is_featured: false, sort_order: 0, category: [], gallery: [] },
  services: { is_published: true, sort_order: 0 },
  client_logos: { is_visible: true, sort_order: 0 },
  testimonials: { is_published: true, sort_order: 0 },
  timeline_entries: { sort_order: 0 },
};

const TABLES_WITH_UPDATED_AT = new Set([
  "projects",
  "services",
  "blog_posts",
  "homepage_content",
  "company_settings",
  "about_content",
]);

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  // nulls/undefined sort last (Postgres NULLS LAST for ASC)
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a) < String(b) ? -1 : 1;
}

/* ------------------------------------------------------------------ */
/* Query builder                                                        */
/* ------------------------------------------------------------------ */

type Operation = "select" | "insert" | "update" | "delete";

class QueryBuilder implements PromiseLike<AnyResult> {
  private table: string;
  private op: Operation = "select";
  private payload: Row | Row[] | null = null;
  private eqFilters: Array<[string, unknown]> = [];
  private neqFilters: Array<[string, unknown]> = [];
  private orders: Array<[string, boolean]> = [];
  private limitCount: number | null = null;
  private singleMode: "maybe" | "strict" | null = null;
  private countMode: "exact" | null = null;
  private headMode = false;

  constructor(table: string) {
    this.table = table;
  }

  select(_columns?: string, opts?: { count?: "exact"; head?: boolean }) {
    if (this.op === "select") {
      this.countMode = opts?.count ?? null;
      this.headMode = opts?.head ?? false;
    }
    return this;
  }

  insert(values: Row | Row[]) {
    this.op = "insert";
    this.payload = values;
    return this;
  }

  update(values: Row) {
    this.op = "update";
    this.payload = values;
    return this;
  }

  delete() {
    this.op = "delete";
    return this;
  }

  eq(column: string, value: unknown) {
    this.eqFilters.push([column, value]);
    return this;
  }

  neq(column: string, value: unknown) {
    this.neqFilters.push([column, value]);
    return this;
  }

  order(column: string, opts?: { ascending?: boolean }) {
    this.orders.push([column, opts?.ascending ?? true]);
    return this;
  }

  limit(n: number) {
    this.limitCount = n;
    return this;
  }

  maybeSingle() {
    this.singleMode = "maybe";
    return this;
  }

  single() {
    this.singleMode = "strict";
    return this;
  }

  then<R1 = AnyResult, R2 = never>(
    onfulfilled?: ((value: AnyResult) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected);
  }

  private buildQuery() {
    const constraints: QueryConstraint[] = this.eqFilters.map(([col, val]) => where(col, "==", val));
    return query(collection(firestore, this.table), ...constraints);
  }

  private async fetchRows(): Promise<Row[]> {
    const snap = await getDocs(this.buildQuery());
    let rows: Row[] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    for (const [col, val] of this.neqFilters) {
      rows = rows.filter((r) => r[col] !== val);
    }
    if (this.orders.length > 0) {
      const orders = this.orders;
      rows.sort((a, b) => {
        for (const [col, asc] of orders) {
          const cmp = compareValues(a[col], b[col]);
          if (cmp !== 0) return asc ? cmp : -cmp;
        }
        return 0;
      });
    }
    if (this.limitCount !== null) rows = rows.slice(0, this.limitCount);
    return rows;
  }

  private async run(): Promise<AnyResult> {
    try {
      switch (this.op) {
        case "select":
          return await this.runSelect();
        case "insert":
          return await this.runInsert();
        case "update":
          return await this.runUpdate();
        case "delete":
          return await this.runDelete();
      }
    } catch (e) {
      // Mirror RLS behaviour: a denied read returns empty data, not an exception.
      if (this.op === "select" && isPermissionDenied(e)) {
        if (this.headMode) return { data: null, error: null, count: 0 };
        return { data: this.singleMode ? null : [], error: null, count: 0 };
      }
      return { data: null, error: toError(e), count: null };
    }
  }

  private async runSelect(): Promise<AnyResult> {
    if (this.headMode && this.countMode) {
      if (this.neqFilters.length === 0) {
        const agg = await getCountFromServer(this.buildQuery());
        return { data: null, error: null, count: agg.data().count };
      }
      const rows = await this.fetchRows();
      return { data: null, error: null, count: rows.length };
    }

    const rows = await this.fetchRows();

    if (this.singleMode) {
      if (rows.length === 0) {
        if (this.singleMode === "strict") {
          return { data: null, error: { message: "No rows found" }, count: 0 };
        }
        return { data: null, error: null, count: 0 };
      }
      return { data: rows[0], error: null, count: 1 };
    }

    return { data: rows, error: null, count: rows.length };
  }

  private async runInsert(): Promise<Result<Row[] | null>> {
    const items = Array.isArray(this.payload) ? this.payload : [this.payload as Row];
    const inserted: Row[] = [];
    for (const item of items) {
      const docRef = doc(collection(firestore, this.table));
      const now = new Date().toISOString();
      const data: Row = {
        ...(INSERT_DEFAULTS[this.table] ?? {}),
        ...item,
        id: docRef.id,
        created_at: (item.created_at as string) ?? now,
        ...(TABLES_WITH_UPDATED_AT.has(this.table) ? { updated_at: now } : {}),
      };
      await setDoc(docRef, data);
      inserted.push(data);
    }
    return { data: inserted, error: null, count: inserted.length };
  }

  private targetDocIds(): string[] | null {
    // Fast path: all mutations in this app target rows by id.
    const idFilter = this.eqFilters.find(([col]) => col === "id");
    if (idFilter && this.eqFilters.length === 1 && this.neqFilters.length === 0) {
      return [String(idFilter[1])];
    }
    return null;
  }

  private async resolveTargetIds(): Promise<string[]> {
    const direct = this.targetDocIds();
    if (direct) return direct;
    const rows = await this.fetchRows();
    return rows.map((r) => String(r.id));
  }

  private async runUpdate(): Promise<Result<null>> {
    const ids = await this.resolveTargetIds();
    const patch: Row = { ...(this.payload as Row) };
    delete patch.id;
    if (TABLES_WITH_UPDATED_AT.has(this.table)) patch.updated_at = new Date().toISOString();
    await Promise.all(ids.map((id) => updateDoc(doc(firestore, this.table, id), patch)));
    return { data: null, error: null, count: ids.length };
  }

  private async runDelete(): Promise<Result<null>> {
    const ids = await this.resolveTargetIds();
    await Promise.all(ids.map((id) => deleteDoc(doc(firestore, this.table, id))));
    return { data: null, error: null, count: ids.length };
  }
}

/* ------------------------------------------------------------------ */
/* Auth                                                                 */
/* ------------------------------------------------------------------ */

// Resolves once Firebase has restored (or determined absence of) the session.
const firstAuthState = new Promise<FirebaseUser | null>((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, (u) => {
    resolve(u);
    unsubscribe();
  });
});

const authApi = {
  async getSession(): Promise<{ data: { session: Session | null } }> {
    await firstAuthState;
    const u = auth.currentUser;
    return { data: { session: u ? { user: mapUser(u) } : null } };
  },

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      callback(u ? "SIGNED_IN" : "SIGNED_OUT", u ? { user: mapUser(u) } : null);
    });
    return { data: { subscription: { unsubscribe } } };
  },

  async signInWithPassword({ email, password }: { email: string; password: string }) {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = mapUser(cred.user);
      return { data: { user, session: { user } }, error: null as BackendError | null };
    } catch (e) {
      const err = toError(e);
      if (err.code === "auth/invalid-credential" || err.code === "auth/wrong-password" || err.code === "auth/user-not-found") {
        err.message = "Invalid login credentials";
      }
      return { data: { user: null, session: null }, error: err };
    }
  },

  async signOut() {
    try {
      await firebaseSignOut(auth);
      return { error: null as BackendError | null };
    } catch (e) {
      return { error: toError(e) };
    }
  },
};

/* ------------------------------------------------------------------ */
/* Storage                                                              */
/* ------------------------------------------------------------------ */

interface StorageListItem {
  name: string;
  id: string;
  created_at: string;
}

const storageApi = {
  from(bucket: string) {
    const fullPath = (path: string) => `${bucket}/${path}`;
    return {
      async upload(
        path: string,
        data: Blob | File,
        opts?: { cacheControl?: string; contentType?: string; upsert?: boolean }
      ) {
        try {
          const maxAge = opts?.cacheControl ?? "3600";
          await uploadBytes(storageRef(storage, fullPath(path)), data, {
            contentType: opts?.contentType || (data as File).type || undefined,
            cacheControl: `public, max-age=${maxAge}`,
          });
          return { data: { path }, error: null as BackendError | null };
        } catch (e) {
          return { data: null, error: toError(e) };
        }
      },

      getPublicUrl(path: string) {
        const full = fullPath(path);
        // media/ dosyalari kendi alan adimizdan sunulur (Hosting rewrite -> media
        // fonksiyonu): bazi aglar firebasestorage.googleapis.com'u engelliyor.
        if (full.startsWith("media/")) {
          const rel = full.slice("media/".length).split("/").map(encodeURIComponent).join("/");
          return { data: { publicUrl: `https://www.csg.com.tr/media/${rel}` } };
        }
        const encoded = encodeURIComponent(full);
        return {
          data: {
            publicUrl: `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_STORAGE_BUCKET}/o/${encoded}?alt=media`,
          },
        };
      },

      async remove(paths: string[]) {
        try {
          await Promise.all(paths.map((p) => deleteObject(storageRef(storage, fullPath(p)))));
          return { data: paths.map((p) => ({ name: p })), error: null as BackendError | null };
        } catch (e) {
          return { data: null, error: toError(e) };
        }
      },

      async list(
        prefix = "",
        opts?: { limit?: number; sortBy?: { column: string; order: "asc" | "desc" } }
      ): Promise<{ data: StorageListItem[] | null; error: BackendError | null }> {
        try {
          const dir = prefix ? `${bucket}/${prefix}` : bucket;
          const res = await listAll(storageRef(storage, dir));
          const items = await Promise.all(
            res.items.map(async (item) => {
              const meta = await getMetadata(item);
              return { name: item.name, id: item.fullPath, created_at: meta.timeCreated };
            })
          );
          const column = (opts?.sortBy?.column ?? "name") as keyof StorageListItem;
          const desc = opts?.sortBy?.order === "desc";
          items.sort((a, b) => {
            const cmp = compareValues(a[column], b[column]);
            return desc ? -cmp : cmp;
          });
          return { data: opts?.limit ? items.slice(0, opts.limit) : items, error: null };
        } catch (e) {
          return { data: null, error: toError(e) };
        }
      },
    };
  },
};

/* ------------------------------------------------------------------ */
/* Functions                                                            */
/* ------------------------------------------------------------------ */

const kebabToCamel = (s: string) => s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

const functionsApi = {
  async invoke(name: string, opts?: { body?: unknown }) {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const u = auth.currentUser;
      if (u) headers.Authorization = `Bearer ${await u.getIdToken()}`;
      const res = await fetch(`${FUNCTIONS_BASE_URL}/${kebabToCamel(name)}`, {
        method: "POST",
        headers,
        body: JSON.stringify(opts?.body ?? {}),
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let data: any = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON response */
      }
      if (!res.ok) {
        const message =
          (data && typeof data === "object" && "error" in data && String((data as { error: unknown }).error)) ||
          `Function ${name} failed with status ${res.status}`;
        return { data, error: { message } as BackendError };
      }
      return { data, error: null as BackendError | null };
    } catch (e) {
      return { data: null, error: toError(e) };
    }
  },
};

/* ------------------------------------------------------------------ */
/* Realtime (Firestore snapshots)                                       */
/* ------------------------------------------------------------------ */

interface RealtimeChannel {
  on(
    type: string,
    filter: { event?: string; schema?: string; table: string },
    callback: () => void
  ): RealtimeChannel;
  subscribe(): RealtimeChannel;
  _unsubscribers: Array<() => void>;
}

function createChannel(): RealtimeChannel {
  const registrations: Array<{ table: string; callback: () => void }> = [];
  const channel: RealtimeChannel = {
    _unsubscribers: [],
    on(_type, filter, callback) {
      registrations.push({ table: filter.table, callback });
      return channel;
    },
    subscribe() {
      for (const reg of registrations) {
        let first = true;
        const unsubscribe = onSnapshot(
          collection(firestore, reg.table),
          () => {
            // Skip the initial snapshot: postgres_changes only fired on changes.
            if (first) {
              first = false;
              return;
            }
            reg.callback();
          },
          () => {
            /* permission errors after sign-out are expected; ignore */
          }
        );
        channel._unsubscribers.push(unsubscribe);
      }
      return channel;
    },
  };
  return channel;
}

/* ------------------------------------------------------------------ */
/* Public client                                                        */
/* ------------------------------------------------------------------ */

export const backend = {
  from: (table: string) => new QueryBuilder(table),
  auth: authApi,
  storage: storageApi,
  functions: functionsApi,
  channel: (_name: string) => createChannel(),
  removeChannel: (channel: RealtimeChannel) => {
    channel._unsubscribers.forEach((unsubscribe) => unsubscribe());
    channel._unsubscribers = [];
  },
};
